import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { Barrel } from "../models/Barrel";
import { BARREL_INITIAL_VALUES } from "../models/Barrel";
import { useBarrels, useStands } from "../hooks/useBarrels";
import { useDeleteBarrel, useUpsertBarrel } from "../hooks/useBarrelMutations";
import { BarrelsTable } from "./BarrelsTable";
import { BarrelDetailViewer } from "./BarrelDetailViewer";
import { BarrelsToolbar } from "./BarrelsToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	standId: urlParam.int(),
	barrelId: urlParam.int(),
};

export const BarrelsManager = () => {
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [barrelToDelete, setBarrelToDelete] = useState<Barrel | null>(null);

	// Filters and the selected barrel, all in the query string.
	const [
		{ search, onlyActives, standId, barrelId },
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows 300 ms later, which is also what stops the
	// list procedure from being called once per keystroke.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	// A link to a stand that is no longer offered falls back to all of them.
	const { data: standOptions = [], isSuccess: standOptionsLoaded } =
		useStands();
	useDropUnknownValue(
		standId,
		standOptionsLoaded,
		standOptions.map((option) => option.Id),
		useCallback(() => dropValues("standId"), [dropValues]),
	);

	// Resizable panels layout persistence
	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "barrels-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	// Data fetching
	const {
		barrels,
		layouts,
		isLoading,
		error: queryError,
	} = useBarrels({
		search: search || null,
		standId,
		includeLayout: true,
	});

	const visibleBarrels = useMemo(
		() => (onlyActives ? barrels.filter((b) => b.Active) : barrels),
		[barrels, onlyActives],
	);

	// The id travels and the barrel is resolved from what the server returned. A barrel being
	// added has no id yet, so it stays out of the URL.
	const selection = useUrlSelection<Barrel>({
		id: barrelId,
		setId: (value) => setFilters({ barrelId: value }),
		dropId: () => dropValues("barrelId"),
		rows: barrels,
		ready: !isLoading,
		getId: (barrel) => barrel.Id,
	});
	const selectedBarrel = selection.current;

	// Mutations
	const { upsertBarrel, isUpserting } = useUpsertBarrel({
		onSuccess: (data) => {
			setNotification(
				selection.isNew
					? "Barrel created successfully"
					: "Barrel updated successfully",
			);
			// The saved barrel stays on screen, and the URL now points at it.
			if (data) selection.adopt(data);
		},
		onError: (err) => setError(err.message),
	});

	const { deleteBarrel, isDeleting } = useDeleteBarrel({
		onSuccess: () => {
			setNotification("Barrel deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setBarrelToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	// Handlers
	const handleRowSelected = useCallback(
		(barrel: Barrel | null) => {
			if (barrel) selection.select(barrel);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((barrel: Barrel) => {
		setBarrelToDelete(barrel);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (barrelToDelete?.Id) {
			deleteBarrel(barrelToDelete.Id);
		}
	}, [barrelToDelete, deleteBarrel]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setBarrelToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		// Deselect current and show empty form for new barrel
		selection.startNew({ ...BARREL_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(barrel: Barrel) => {
			upsertBarrel(barrel);
		},
		[upsertBarrel],
	);

	// Inline Active toggle from the list: the row is a complete barrel, so a full upsert
	// with the flag flipped keeps every other field.
	const handleToggleActive = useCallback(
		(barrel: Barrel, active: boolean) => {
			upsertBarrel({ ...barrel, Active: active });
		},
		[upsertBarrel],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	// Get layouts for the selected barrel
	const currentBarrelLayouts = selectedBarrel?.Id
		? layouts.filter((l) => l.BarrelId === selectedBarrel.Id)
		: [];

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Barrels
			</Typography>

			<Paper elevation={3}>
				<BarrelsToolbar
					search={searchText}
					onSearchChange={setSearchText}
					standId={standId}
					onStandChange={(value) => setFilters({ standId: value })}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="barrels-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<BarrelsTable
									barrels={visibleBarrels}
									selectedBarrel={selectedBarrel?.Id ? selectedBarrel : null}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
									onToggleActive={handleToggleActive}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<BarrelDetailViewer
								barrel={selectedBarrel}
								existingBarrels={barrels}
								layouts={currentBarrelLayouts}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE BARREL"
				itemName={barrelToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
